import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./App.css";
import { ServerUrl } from "../config";
import { iotControlData } from "../data/NewControlData";

import UtteranceTrackerModal from "./UtteranceTrackerModal";

const LANG_KEYS = ["spanish", "english", "french", "german", "italian", "dutch"];
const LANG_LABEL = {
  spanish: "Spanish",
  english: "English",
  french: "French",
  german: "German",
  italian: "Italian",
  dutch: "Dutch",
};
// Fallback: guess the language a contributor is assigned from their email (e.g. lb_de_01 -> German).
const LANG_BY_PREFIX = { es: "spanish", en: "english", fr: "french", de: "german", it: "italian", nl: "dutch" };

const normEmail = (s) => (s || "").toLowerCase().trim();

// Loose match for free-text utterances: lowercase, collapse whitespace, drop trailing
// punctuation. Lets a saved `transcribedText` ("  Schalte ... ein.") match a data-file entry
// ("Schalte ... ein") even when `sampleUtterance` / `userInput` were left blank on upload.
const utext = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[.!?。！？]+$/g, "")
    .trim();

// Accent / space / punctuation-insensitive fallback. STT output rarely matches the data file
// character-for-character (extra spaces, hyphens, mid-string commas, "ä" vs "ae"), so a second
// pass compares only the letters+digits: "Schalte die Dachbodenbeleuchtung ein." -> "schaltediedachbodenbeleuchtungein".
const utextTight = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD") // "ü" -> "u" + combining mark, which the next strip drops
    .replace(/[^a-z0-9]+/g, "");

// Every field an utterance might have landed in, best (explicit pick) first.
const utteranceCandidates = (a) => [a.sampleUtterance, a.userInput, a.transcribedText, a.translation];

const utterancesFor = (cat, vc, lang) => {
  const m = iotControlData?.[cat]?.[vc];
  if (!m) return [];
  return m[lang] || m["spanish"] || m["english"] || [];
};

// Expected Sample Utterances per language, one entry per catalog slot:
// { loose: "vc||utterance", tight: "lettersAndDigitsOnly" }. total = number of slots.
const EXPECTED_BY_LANG = (() => {
  const out = {};
  LANG_KEYS.forEach((lang) => {
    const slots = [];
    Object.keys(iotControlData || {}).forEach((cat) => {
      Object.keys(iotControlData[cat] || {}).forEach((vc) => {
        utterancesFor(cat, vc, lang).forEach((u) => {
          slots.push({ loose: `${utext(vc)}||${utext(u)}`, tight: utextTight(u) });
        });
      });
    });
    out[lang] = { total: slots.length, slots };
  });
  return out;
})();

// How many of `lang`'s expected slots are covered by `audios`. A slot is covered if any
// field of any recording loosely or tightly matches it. Re-recordings of the same slot
// count once; extra recordings that match no slot are ignored. Result is 0..total.
const coveredCount = (audios, lang) => {
  const exp = EXPECTED_BY_LANG[lang];
  if (!exp) return 0;
  const loose = new Set();
  const tight = new Set();
  (audios || []).forEach((a) => {
    const vc = utext(a.audiotext);
    utteranceCandidates(a).forEach((c) => {
      const l = utext(c);
      if (l) loose.add(`${vc}||${l}`);
      const t = utextTight(c);
      if (t) tight.add(t);
    });
  });
  let n = 0;
  exp.slots.forEach((s) => {
    if (loose.has(s.loose) || (s.tight && tight.has(s.tight))) n += 1;
  });
  return n;
};

function AdminPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleSettings, setRoleSettings] = useState([]);
  const [adminStats, setAdminStats] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState("users");
  const [isTrackerOpen, setIsTrackerOpen] = useState(false);
  const [trackerUserEmail, setTrackerUserEmail] = useState("");
  const [audios, setAudios] = useState([]);

  const permissionsString = localStorage.getItem("permissions");
  let permissions = null;
  try {
    permissions = permissionsString ? JSON.parse(permissionsString) : null;
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }

  const userRole = localStorage.getItem("role") || "user";
  const isSuper = userRole === "superadmin";

  // Redirect users who cannot manage users
  useEffect(() => {
    if (!permissions || !permissions.canManageUsers) {
      Swal.fire("Access Denied", "You do not have permission to access user management.", "error");
      navigate("/audio");
    }
  }, [permissions, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setUsers(response.data);
      } else {
        throw new Error("Failed to fetch users");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAudioCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}audios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setAudios(response.data?.data || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setAudios([]);
      } else {
        console.error("Failed to fetch audio counts:", error);
      }
    }
  };

  // Per-contributor progress: total samples + distinct collected utterances for their language.
  const userProgress = useMemo(() => {
    const acc = {};
    audios.forEach((a) => {
      const email = normEmail(a.uploaderEmail || a.email);
      if (!email) return;
      if (!acc[email]) acc[email] = { samples: 0, langCount: {}, audiosByLang: {} };
      const rec = acc[email];
      rec.samples += 1;
      const lang = normEmail(a.audioLanguage);
      if (lang) {
        rec.langCount[lang] = (rec.langCount[lang] || 0) + 1;
        if (!rec.audiosByLang[lang]) rec.audiosByLang[lang] = [];
        rec.audiosByLang[lang].push(a);
      }
    });

    const out = {};
    Object.entries(acc).forEach(([email, rec]) => {
      const top = Object.entries(rec.langCount).sort((x, y) => y[1] - x[1])[0];
      const prefix = /lb_([a-z]{2})_/i.exec(email);
      const lang = (top && top[1] > 0 && top[0]) || (prefix && LANG_BY_PREFIX[prefix[1].toLowerCase()]) || null;

      let collected = 0;
      let total = 0;
      if (lang && EXPECTED_BY_LANG[lang]) {
        total = EXPECTED_BY_LANG[lang].total;
        collected = coveredCount(rec.audiosByLang[lang], lang);
      }
      out[email] = {
        samples: rec.samples,
        lang,
        collected,
        total,
        percent: total > 0 ? Math.round((collected / total) * 100) : 0,
      };
    });
    return out;
  }, [audios]);

  const fetchRoleSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/role-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setRoleSettings(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch role settings:", error);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${ServerUrl}api/admin-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setAdminStats(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch admin stats:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAudioCounts();
    if (isSuper) {
      fetchRoleSettings();
      fetchAdminStats();
    }
  }, [isSuper]);

  const updateRole = async (email, currentRole) => {
    const roleOptions = {
      user: "User",
      admin: "Admin",
      superadmin: "Super Admin"
    };

    const { value: newRole } = await Swal.fire({
      title: "Update User Role",
      input: "select",
      inputOptions: roleOptions,
      inputValue: currentRole,
      showCancelButton: true,
      inputPlaceholder: "Select a role",
      inputValidator: (value) => {
        if (!value) {
          return "You need to select a role";
        }
      }
    });

    if (newRole && newRole !== currentRole) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${ServerUrl}api/updateuser`, {
          email,
          role: newRole,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          Swal.fire("Success", "User role updated successfully", "success");
          setUsers(users.map((user) =>
            user.email === email ? { ...user, role: newRole } : user
          ));
        } else {
          throw new Error("Failed to update role");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to update role", "error");
      }
    }
  };

  const resetPassword = async (email) => {
    const { value: newPassword } = await Swal.fire({
      title: `Reset password for ${email}`,
      input: "password",
      inputLabel: "New Password",
      inputPlaceholder: "Enter new password",
      inputAttributes: {
        autocapitalize: "off",
        autocorrect: "off"
      },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value) {
          return "You need to write a password!";
        }
      }
    });

    if (newPassword) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${ServerUrl}api/reset-password`,
          { email, password: newPassword },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status === 200) {
          Swal.fire("Success", "Password reset successfully!", "success");
        } else {
          throw new Error("Failed to reset password");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to reset password", "error");
      }
    }
  };

  const handleDeleteUser = async (email) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete user: ${email}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete"
    });

    if (confirm.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.delete(`${ServerUrl}api/deleteuser`, {
          data: { email },
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200) {
          Swal.fire("Deleted!", "User deleted successfully", "success");
          fetchUsers();
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to delete user", "error");
      }
    }
  };

  const handleBack = () => {
    navigate("/audio");
  };

  const handleRegisterUser = async () => {
    const roleSelectHtml = isSuper 
      ? '<select id="swal-input3" class="swal2-select" style="border: 1px solid black; width: 80%; padding: 8px; margin: 10px 0; font-size: 16px;">' +
        '<option value="user">User</option>' +
        '<option value="admin">Admin</option>' +
        '<option value="superadmin">Super Admin</option>' +
        "</select>"
      : '<select id="swal-input3" class="swal2-select" style="border: 1px solid black; width: 80%; padding: 8px; margin: 10px 0; font-size: 16px;">' +
        '<option value="user">User</option>' +
        "</select>";

    const { value: formValues } = await Swal.fire({
      title: "Register New User",
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Email" type="email">' +
        '<input id="swal-input2" class="swal2-input" placeholder="Password" type="password">' +
        roleSelectHtml,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Register",
      preConfirm: () => {
        const email = document.getElementById("swal-input1").value;
        const password = document.getElementById("swal-input2").value;
        const role = document.getElementById("swal-input3").value;

        if (!email || !password || !role) {
          Swal.showValidationMessage("Please fill in all fields");
          return false;
        }
        return { email, password, role };
      },
    });

    if (formValues) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(`${ServerUrl}api/register`, {
          email: formValues.email,
          password: formValues.password,
          role: formValues.role,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 201 || response.status === 200) {
          Swal.fire("Success", response.data.message || "User registered successfully", "success");
          fetchUsers();
        } else {
          throw new Error("Failed to register user");
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to register user", "error");
      }
    }
  };

  const handleToggleTab = (role, tab) => {
    setRoleSettings(roleSettings.map(s => {
      if (s.role === role) {
        const visibleTabs = s.visibleTabs.includes(tab)
          ? s.visibleTabs.filter(t => t !== tab)
          : [...s.visibleTabs, tab];
        return { ...s, visibleTabs };
      }
      return s;
    }));
  };

  const handleTogglePermission = (role, permission) => {
    setRoleSettings(roleSettings.map(s => {
      if (s.role === role) {
        return {
          ...s,
          permissions: {
            ...s.permissions,
            [permission]: !s.permissions[permission]
          }
        };
      }
      return s;
    }));
  };

  const handleSaveSettings = async (role) => {
    const setting = roleSettings.find(s => s.role === role);
    if (!setting) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${ServerUrl}api/role-settings/${role}`, {
        visibleTabs: setting.visibleTabs,
        permissions: setting.permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        Swal.fire("Success", `Role layout settings updated for ${role}`, "success");
        // If updating self, update active localStorage configuration
        if (role === userRole) {
          localStorage.setItem("visibleTabs", JSON.stringify(setting.visibleTabs));
          localStorage.setItem("permissions", JSON.stringify(setting.permissions));
        }
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to save settings", "error");
    }
  };

  return (
    <div className="w-screen h-screen bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 flex justify-center p-3 sm:p-6 overflow-hidden">
      <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-xl w-full max-w-6xl flex flex-col gap-4 min-h-0">
        <h1 className="text-xl sm:text-2xl font-semibold text-center text-gray-800 shrink-0">Admin Panel</h1>

        <div className="flex justify-between items-center flex-wrap gap-3 shrink-0">
          <button onClick={handleBack} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-sm">
            Back to Home
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                setTrackerUserEmail("");
                setIsTrackerOpen(true);
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 text-sm font-semibold flex items-center gap-1 shadow-sm"
            >
              📊 Utterances Progress & Pending Tracker
            </button>
            {activeAdminTab === "users" && (
              <button onClick={handleRegisterUser} className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 text-sm font-semibold">
                Register User
              </button>
            )}
          </div>
        </div>

        {/* Tab Navigation for Superadmin */}
        {isSuper && (
          <div className="flex border-b border-gray-200 shrink-0">
            <button
              onClick={() => setActiveAdminTab("users")}
              className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
                activeAdminTab === "users"
                  ? "border-b-2 border-blue-500 text-blue-500 font-bold"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              👥 User Management
            </button>
            <button
              onClick={() => setActiveAdminTab("stats")}
              className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
                activeAdminTab === "stats"
                  ? "border-b-2 border-blue-500 text-blue-500 font-bold"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📊 Admin Stats
            </button>
          </div>
        )}

        {activeAdminTab === "users" && (
          loading ? (
            <div className="loader mx-auto"></div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-0">
              <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 shrink-0">
                <h3 className="text-sm font-bold text-gray-700">Users ({users.length})</h3>
              </div>
              <div className="flex-1 min-h-0 overflow-auto">
              <table className="min-w-full bg-white text-sm">
                <thead>
                  <tr className="text-gray-700 uppercase font-semibold">
                    {["Email", "Role", "Progress (collected / total)", "Actions"].map((h, i) => (
                      <th
                        key={h}
                        className={`sticky top-0 z-10 bg-gray-100 py-3 px-4 ${i < 2 ? "text-left" : "text-center"}`}
                        style={{ boxShadow: "inset 0 -1px 0 #e5e7eb" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const p = userProgress[(user.email || "").toLowerCase().trim()] || {
                      samples: 0, lang: null, collected: 0, total: 0, percent: 0,
                    };
                    return (
                    <tr key={user._id} className="text-gray-700 hover:bg-gray-50 transition-colors border-b align-middle">
                      <td className="py-3 px-4 font-medium max-w-[16rem] truncate">{user.email}</td>
                      <td className="py-3 px-4 capitalize font-medium whitespace-nowrap">{user.role}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col items-center gap-1 min-w-[9rem]">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-base font-bold text-indigo-700">
                              {p.total > 0 ? `${p.collected} / ${p.total}` : p.samples}
                            </span>
                            {p.total > 0 && (
                              <span className="text-xs font-semibold text-gray-400">{p.percent}%</span>
                            )}
                          </div>
                          {p.total > 0 && (
                            <div className="h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${Math.min(100, p.percent)}%`,
                                  background: p.percent >= 100 ? "#10b981" : p.percent >= 50 ? "#6366f1" : "#f59e0b",
                                }}
                              />
                            </div>
                          )}
                          {/* <span className="text-[10px] uppercase tracking-wide text-gray-400">
                            {p.lang ? LANG_LABEL[p.lang] || p.lang : `${p.samples} samples`}
                          </span>
                          {(() => {
                            let text = "No target set";
                            let color = "#94a3b8";
                            if (p.total > 0) {
                              if (p.collected === 0) { text = "Not started"; color = "#dc2626"; }
                              else if (p.percent >= 100) { text = "Completed"; color = "#059669"; }
                              else if (p.percent >= 50) { text = "In progress"; color = "#2563eb"; }
                              else { text = "Just started"; color = "#d97706"; }
                            }
                            return (
                              <span className="text-[11px] font-semibold italic" style={{ color }}>
                                {text}
                              </span>
                            );
                          })()} */}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-center items-center gap-2 flex-wrap">
                          <button
                            onClick={() => {
                              setTrackerUserEmail(user.email);
                              setIsTrackerOpen(true);
                            }}
                            className="bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700 text-xs font-semibold whitespace-nowrap"
                          >
                            View Progress
                          </button>
                          {isSuper && (
                            <button
                              onClick={() => updateRole(user.email, user.role)}
                              className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 text-xs font-semibold whitespace-nowrap"
                            >
                              Update Role
                            </button>
                          )}
                          <button
                            onClick={() => resetPassword(user.email)}
                            className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600 text-xs font-semibold whitespace-nowrap"
                          >
                            Reset Password
                          </button>
                          {user.role !== "superadmin" && (
                            <button
                              onClick={() => handleDeleteUser(user.email)}
                              className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 text-xs font-semibold whitespace-nowrap"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )
        )}

        {activeAdminTab === "settings" && (
          /* Settings configuration tab content */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 min-h-0 overflow-y-auto">
            {roleSettings.map((setting) => (
              <div key={setting.role} className="border border-gray-200 rounded-xl p-5 shadow-md bg-gray-50 space-y-4">
                <h3 className="text-lg font-bold text-gray-800 capitalize border-b pb-2">
                  Role: {setting.role === "superadmin" ? "Super Admin" : setting.role === "admin" ? "Admin" : "User"}
                </h3>
                
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Visible Tabs</h4>
                  <div className="space-y-1">
                    {["audio", "channel", "app"].map((tab) => (
                      <label key={tab} className="flex items-center text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={setting.visibleTabs.includes(tab)}
                          onChange={() => handleToggleTab(setting.role, tab)}
                          className="mr-2 rounded text-blue-500 focus:ring-blue-400"
                        />
                        {tab === "audio" ? "🔊 Audio Upload" : tab === "channel" ? "📺 Channel Upload" : "📱 App Upload"}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Permissions</h4>
                  <div className="space-y-1">
                    {[
                      { key: "canManageUsers", label: "👥 Manage Users (Admin Panel)" },
                      { key: "canUploadAudio", label: "🔊 Upload / Record Audio" },
                      { key: "canUploadChannels", label: "📺 Upload / Manage Channels" },
                      { key: "canUploadApps", label: "📱 Upload / Manage Apps" }
                    ].map((perm) => (
                      <label key={perm.key} className="flex items-center text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={setting.permissions[perm.key]}
                          onChange={() => handleTogglePermission(setting.role, perm.key)}
                          className="mr-2 rounded text-blue-500 focus:ring-blue-400"
                        />
                        {perm.label}
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => handleSaveSettings(setting.role)}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 rounded-lg transition-colors text-sm"
                >
                  Save Settings
                </button>
              </div>
            ))}
          </div>
        )}

        {activeAdminTab === "stats" && (
          <div className="overflow-auto border border-gray-200 rounded-lg flex-1 min-h-0">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="text-gray-700 uppercase font-semibold">
                  {["Admin Email", "Users Registered", "Total Audios"].map((h, i) => (
                    <th
                      key={h}
                      className={`sticky top-0 z-20 bg-gray-200 py-3 px-4 ${i === 0 ? "text-left" : "text-center"}`}
                      style={{ boxShadow: "inset 0 -1px 0 #d1d5db" }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminStats.map((stat, idx) => (
                  <tr key={idx} className="text-gray-700 hover:bg-gray-50 transition-colors border-b">
                    <td className="py-3 px-4 truncate">{stat.adminEmail}</td>
                    <td className="py-3 px-4 text-center">{stat.usersRegistered}</td>
                    <td className="py-3 px-4 text-center">{stat.totalAudios}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <UtteranceTrackerModal
          isOpen={isTrackerOpen}
          onClose={() => {
            setIsTrackerOpen(false);
            fetchAudioCounts();
          }}
          targetUserEmail={trackerUserEmail}
        />
      </div>
    </div>
  );
}

export default AdminPage;